<template>
    <div class="main">
        <c-panel title="PORTAL DO GESTOR PÚBLICO" titleIcon="fas fa-chart-line">
            <el-alert type="info" :closable="false" class="u-mb-15"
                title="Indicadores consolidados diariamente"
                description="Indicador sem base de cálculo no dia não é publicado como zero — fica ausente do painel."></el-alert>

            <el-form label-position="top" @submit.native.prevent>
                <el-row :gutter="10" class="u-mb-15">
                    <el-col :xs="24" :sm="6">
                        <el-form-item label="Período letivo" class="u-mb-0">
                            <el-select v-model="periodoId" style="width: 100%;" @change="carregar">
                                <el-option v-for="p in periodos" :key="p.id" :value="p.id" :label="p.nome"></el-option>
                            </el-select>
                        </el-form-item>
                    </el-col>
                    <el-col :xs="24" :sm="8">
                        <el-form-item label="Escopo" class="u-mb-0">
                            <el-select v-model="instituicaoId" clearable filterable style="width: 100%;"
                                placeholder="Rede municipal (consolidado)" @change="carregar">
                                <el-option v-for="i in instituicoes" :key="i.id" :value="i.id" :label="i.nome"></el-option>
                            </el-select>
                        </el-form-item>
                    </el-col>
                    <el-col :xs="24" :sm="10" class="u-text-right u-mt-25">
                        <el-button v-if="podeConsolidar" type="primary" size="small" icon="el-icon-refresh" @click="consolidar">
                            CONSOLIDAR AGORA
                        </el-button>
                        <el-button class="u-ml-10" size="small" icon="el-icon-printer" :disabled="!painel || !painel.data_referencia" @click="imprimir">IMPRIMIR</el-button>
                    </el-col>
                </el-row>
            </el-form>

            <el-alert v-if="painel && painel.aviso" type="warning" :closable="false" class="u-mb-15"
                :title="painel.aviso"></el-alert>

            <!-- ==================== KPIs ==================== -->
            <div v-if="painel && painel.indicadores.length">
                <div class="u-mb-10">
                    <small class="u-text-muted">
                        Consolidação de {{ dataBr(painel.data_referencia) }}
                        <span v-if="painel.data_comparacao"> · comparado com {{ dataBr(painel.data_comparacao) }}</span>
                    </small>
                </div>

                <el-row :gutter="10" class="u-mb-20">
                    <el-col v-for="i in painel.indicadores" :key="i.indicador" :xs="12" :sm="6">
                        <div class="kpi" :class="{ 'kpi-ativo': i.indicador === indicadorSerie }" @click="verSerie(i.indicador)">
                            <span class="n">{{ formatar(i) }}</span>
                            <span class="r">{{ i.rotulo }}</span>
                            <span class="v">
                                <span v-if="i.variacao === null" class="flat">sem comparação anterior</span>
                                <span v-else-if="i.variacao > 0" class="up">▲ {{ i.variacao }}</span>
                                <span v-else-if="i.variacao < 0" class="down">▼ {{ Math.abs(i.variacao) }}</span>
                                <span v-else class="flat">estável</span>
                            </span>
                        </div>
                    </el-col>
                </el-row>

                <!-- ==================== SÉRIE HISTÓRICA ==================== -->
                <h4 class="u-mb-10">
                    Série histórica
                    <small v-if="serie" class="u-text-muted">— {{ serie.rotulo }} ({{ serie.pontos }} ponto(s))</small>
                </h4>

                <el-row :gutter="10" class="u-mb-10">
                    <el-col :xs="24" :sm="8">
                        <el-select v-model="indicadorSerie" style="width: 100%;" size="small" @change="carregarSerie">
                            <el-option v-for="i in painel.indicadores" :key="i.indicador" :value="i.indicador" :label="i.rotulo"></el-option>
                        </el-select>
                    </el-col>
                    <el-col :xs="24" :sm="6">
                        <el-select v-model="dias" style="width: 100%;" size="small" @change="carregarSerie">
                            <el-option :value="7" label="Últimos 7 dias"></el-option>
                            <el-option :value="30" label="Últimos 30 dias"></el-option>
                            <el-option :value="90" label="Últimos 90 dias"></el-option>
                            <el-option :value="365" label="Último ano"></el-option>
                        </el-select>
                    </el-col>
                </el-row>

                <!--
                    UM PONTO NÃO É UMA LINHA.

                    Com uma única consolidação, o Chart.js desenha um ponto solitário e
                    auto-escala o eixo Y numa janela de ±1 em torno dele — o resultado é um
                    grid vazio, que faz o gestor achar que o gráfico quebrou.

                    A série só vira linha a partir da segunda consolidação. Até lá, o valor
                    já está no cartão acima; aqui se diz por que ainda não há linha.
                -->
                <div v-if="serie && serie.pontos > 1" class="u-mb-25">
                    <!--
                        A linha não pode passar por medição quando não é.
                        `poc:serie-demo` preenche o histórico anterior à primeira consolidação
                        real para que o gráfico exista numa instalação nova; quem olha depois
                        não tem como saber disso pelo desenho.
                    -->
                    <el-alert v-if="serie.pontos_demonstracao > 0" type="warning" :closable="false"
                        show-icon class="u-mb-10" data-test="aviso-serie-demo"
                        title="Série com pontos de demonstração."
                        :description="serie.pontos_demonstracao + ' de ' + serie.pontos + ' pontos foram gerados para ilustrar a linha. O último ponto é a consolidação real.'">
                    </el-alert>

                    <line-chart :chart-data="dadosSerie" :height="260"></line-chart>
                </div>

                <el-alert v-else-if="serie && serie.pontos === 1" type="info" :closable="false"
                    class="u-mb-25" show-icon
                    title="Ainda não há série para desenhar."
                    :description="'Existe uma única consolidação até agora (' + dataBr(serie.labels[0]) + ', com ' + formatarValor(serie.valores[0]) + '). A linha aparece a partir da segunda — a rotina consolida uma vez por dia.'">
                </el-alert>

                <el-alert v-else-if="serie && serie.pontos === 0" type="info" :closable="false"
                    class="u-mb-25" show-icon
                    title="Sem consolidação no período escolhido."
                    description="Aumente a janela de dias ou execute a consolidação."></el-alert>
                <el-alert v-else-if="serie" type="info" :closable="false" class="u-mb-25"
                    title="Ainda não há pontos suficientes nesta série."
                    description="A série se forma com as consolidações diárias — um ponto por dia."></el-alert>

                <!-- ==================== COMPARATIVO ==================== -->
                <h4 class="u-mb-10">Comparativo entre escolas</h4>

                <el-row :gutter="10" class="u-mb-10">
                    <el-col :xs="24" :sm="8">
                        <el-select v-model="indicadorComparativo" style="width: 100%;" size="small" @change="carregarComparativo">
                            <el-option v-for="i in painel.indicadores" :key="i.indicador" :value="i.indicador" :label="i.rotulo"></el-option>
                        </el-select>
                    </el-col>
                </el-row>

                <div v-if="comparativo && comparativo.escolas.length" class="container-table">
                    <table class="table table-striped">
                        <thead>
                            <tr>
                                <th style="width: 70px;" class="u-text-center">#</th>
                                <th>Escola</th>
                                <th style="width: 160px;" class="u-text-center">{{ comparativo.rotulo }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="e in comparativo.escolas" :key="e.instituicao_id">
                                <td class="u-text-center">{{ e.posicao }}</td>
                                <td>{{ e.instituicao }}</td>
                                <td class="u-text-center">
                                    {{ comparativo.unidade === '%' ? e.valor.toFixed(2) + '%' : e.valor }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <el-alert v-else-if="comparativo" type="info" :closable="false"
                    :title="comparativo.aviso || 'Nenhuma escola com este indicador consolidado.'"></el-alert>
            </div>

            <div v-else-if="painel === null" class="u-text-center u-mt-25"><img src="/public/img/loading-bar.gif" /></div>
        </c-panel>
    </div>
</template>
<script>
import LineChart from '@/chart/LineChart.js'

/** BLOCO L — item 1: Portal do Gestor Público. */
import { periodosLetivosMixin } from '@/shared/periodos-letivos'
export default {
    mixins: [periodosLetivosMixin],
    components: { LineChart },
    data: () => ({
        periodoId: null, instituicaoId: null, instituicoes: [],
        painel: null, serie: null, comparativo: null,
        indicadorSerie: 'matriculas_ativas', indicadorComparativo: 'matriculas_ativas', dias: 30
    }),
    computed: {
        /** A consolidação manual é operação de rede — o backend também recusa os demais. */
        podeConsolidar () { return [4, 5].includes(parseInt(this.$store.state.user.tipo)) },
        dadosSerie () {
            if (!this.serie) return { labels: [], datasets: [] }

            return {
                labels: this.serie.labels.map(d => this.dataBr(d).substring(0, 5)),
                datasets: [{
                    label: this.serie.rotulo,
                    borderColor: '#409EFF',
                    backgroundColor: 'rgba(64, 158, 255, 0.15)',
                    fill: true,
                    data: this.serie.valores
                }]
            }
        }
    },
    mounted () {
        this.periodoId = this.$store.state.config.periodo ? this.$store.state.config.periodo.id : null

        this.$axios.get(`${this.$store.state.config.baseUrlApi}/api/instituicao/getAll`,
            this.$store.getters.getHeaderAuth
        ).then(r => { this.instituicoes = Array.isArray(r.data) ? r.data : [] }).catch(() => {})

        this.carregar()
    },
    methods: {
        carregar () {
            this.painel = null
            if (!this.periodoId) return

            const base = `${this.$store.state.config.baseUrlApi}/api/indicador-gestao/painel/${this.periodoId}`
            const url = this.instituicaoId ? `${base}/${this.instituicaoId}` : base

            this.$axios.get(url, this.$store.getters.getHeaderAuth).then(r => {
                if (r.data && r.data.erro) { window.eventBus.$emit('alert', { msg: this.textoErro(r.data.erro), type: 'warning' }); return }
                this.painel = r.data

                if (this.painel.indicadores.length) {
                    const existe = this.painel.indicadores.some(i => i.indicador === this.indicadorSerie)
                    if (!existe) this.indicadorSerie = this.painel.indicadores[0].indicador
                    if (!this.painel.indicadores.some(i => i.indicador === this.indicadorComparativo)) {
                        this.indicadorComparativo = this.painel.indicadores[0].indicador
                    }
                    this.carregarSerie()
                    this.carregarComparativo()
                }
            }).catch(e => this.$store.commit('throwException', e))
        },
        verSerie (indicador) {
            this.indicadorSerie = indicador
            this.carregarSerie()
        },
        carregarSerie () {
            this.serie = null
            if (!this.periodoId || !this.indicadorSerie) return

            const base = `${this.$store.state.config.baseUrlApi}/api/indicador-gestao/serieHistorica/${this.indicadorSerie}/${this.periodoId}`
            const url = this.instituicaoId ? `${base}/${this.instituicaoId}` : base
            const cfg = JSON.parse(JSON.stringify(this.$store.getters.getHeaderAuth))
            cfg.params = { dias: this.dias }

            this.$axios.get(url, cfg).then(r => {
                if (r.data && r.data.erro) return
                this.serie = r.data
            }).catch(e => this.$store.commit('throwException', e))
        },
        carregarComparativo () {
            this.comparativo = null
            if (!this.periodoId) return

            this.$axios.post(`${this.$store.state.config.baseUrlApi}/api/indicador-gestao/comparativoEscolas/${this.periodoId}`,
                { indicador: this.indicadorComparativo }, this.$store.getters.getHeaderAuth
            ).then(r => {
                if (r.data && r.data.erro) return
                this.comparativo = r.data
            }).catch(e => this.$store.commit('throwException', e))
        },
        consolidar () {
            window.eventBus.$emit('loading', true)
            this.$axios.post(`${this.$store.state.config.baseUrlApi}/api/indicador-gestao/consolidar`,
                { periodo_letivo_id: this.periodoId }, this.$store.getters.getHeaderAuth
            ).then(r => {
                window.eventBus.$emit('loading', false)
                if (r.data && r.data.erro) { window.eventBus.$emit('alert', { msg: this.textoErro(r.data.erro), type: 'warning' }); return }
                window.eventBus.$emit('alert', {
                    msg: `${r.data.registros} indicador(es) consolidado(s) em ${r.data.instituicoes} escola(s).`,
                    type: 'success'
                })
                this.carregar()
            }).catch(e => { window.eventBus.$emit('loading', false); this.$store.commit('throwException', e) })
        },
        imprimir () {
            window.eventBus.$emit('print', {
                url: '/api/indicador-gestao/imprimirPainel',
                param: { periodo_letivo_id: this.periodoId, instituicao_id: this.instituicaoId,
                         indicador: this.indicadorComparativo }
            })
        },
        /* O valor da série vem cru; o cartão usa `formatar`, que espera o indicador inteiro. */
        formatarValor (v) {
            return Number(v).toLocaleString('pt-BR')
        },
        formatar (i) {
            if (i.unidade === '%') return i.valor.toFixed(2) + '%'
            return Number(i.valor).toLocaleString('pt-BR')
        },
        dataBr (d) {
            if (!d) return '—'
            const p = d.substring(0, 10).split('-')
            return `${p[2]}/${p[1]}/${p[0]}`
        },
        textoErro (e) { return typeof e === 'string' ? e : (e.text || 'Não foi possível concluir.') }
    }
}
</script>
<style scoped>
.kpi { border: 1px solid #dcdfe6; border-radius: 4px; padding: 12px; text-align: center; margin-bottom: 10px; cursor: pointer; }
.kpi:hover { border-color: #409eff; }
.kpi-ativo { border-color: #409eff; background: #ecf5ff; }
.kpi .n { display: block; font-size: 24px; font-weight: bold; }
.kpi .r { display: block; font-size: 12px; color: #606266; margin-top: 2px; }
.kpi .v { display: block; font-size: 11px; margin-top: 4px; }
.up { color: #2e7d32; } .down { color: #c62828; } .flat { color: #909399; }
</style>
